import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { MerchandiseOrder, PersatuanPaymentSettings } from '../../types';
import { getPersatuanPaymentSettingsDB, DEFAULT_PERSATUAN_PAYMENT_SETTINGS } from '../../services/db';
import { uploadReceiptToCloudinary } from '../../lib/cloudinary';
import { useToast } from '../common/Toast';
import {
  QrCode,
  Copy,
  Check,
  Upload,
  FileCheck,
  Clock,
  AlertCircle,
  Building2,
  ShieldCheck,
  Send,
  X,
  CreditCard,
  Sparkles
} from 'lucide-react';

interface DuitNowQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: MerchandiseOrder | null;
  onPaymentSubmitted: (updatedOrder: MerchandiseOrder) => Promise<void>;
}

export const DuitNowQrModal: React.FC<DuitNowQrModalProps> = ({
  isOpen,
  onClose,
  order,
  onPaymentSubmitted
}) => {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [receiptImage, setReceiptImage] = useState<string>('');
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [isCopiedAcc, setIsCopiedAcc] = useState<boolean>(false);
  const [isCopiedRef, setIsCopiedRef] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      getPersatuanPaymentSettingsDB().then(setSettings).catch(() => {});
      if (order?.paymentRef) {
        setPaymentRef(order.paymentRef);
      } else {
        setPaymentRef('');
      }
      if (order?.paymentProofUrl) {
        setReceiptImage(order.paymentProofUrl);
      } else {
        setReceiptImage('');
      }
    }
  }, [isOpen, order]);

  if (!order) return null;

  const copyToClipboard = (text: string, type: 'acc' | 'ref') => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      if (type === 'acc') {
        setIsCopiedAcc(true);
        setTimeout(() => setIsCopiedAcc(false), 2000);
      } else {
        setIsCopiedRef(true);
        setTimeout(() => setIsCopiedRef(false), 2000);
      }
      showToast('success', 'Disalin!', `${text} telah disalin ke papan keratan.`);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      showToast('error', 'Format Tidak Sah', 'Sila muat naik fail imej (PNG, JPG, JPEG) atau PDF sahaja.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('error', 'Saiz Terlalu Besar', 'Maksimum saiz imej resit ialah 10MB.');
      return;
    }

    setIsUploadingReceipt(true);
    showToast('info', 'Memuat Naik Resit...', 'Menyimpan resit ke Cloudinary...');
    try {
      const res = await uploadReceiptToCloudinary(file, order ? `order-${order.id}` : 'merch');
      setReceiptImage(res.secureUrl);
      showToast('success', 'Resit Disimpan', 'Resit berjaya disimpan ke Cloudinary CDN.');
    } catch (err: any) {
      console.error('Error uploading receipt to Cloudinary:', err);
      showToast('error', 'Gagal Memuat Naik', err?.message || 'Sila cuba lagi.');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleSubmitProof = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentRef.trim() && !receiptImage) {
      showToast('error', 'Maklumat Diperlukan', 'Sila masukkan nombor rujukan transaksi atau muat naik slip resit perbankan.');
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedOrder: MerchandiseOrder = {
        ...order,
        paymentMethod: 'DuitNow QR',
        paymentStatus: 'Menunggu Pengesahan',
        paymentRef: paymentRef.trim() || undefined,
        paymentProofUrl: receiptImage || undefined,
        paidAt: new Date().toISOString()
      };

      await onPaymentSubmitted(updatedOrder);
      showToast('success', 'Bukti Bayaran Dihantar!', 'Maklumat bayaran QR anda berjaya dihantar untuk pengesahan penjual.');
      onClose();
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan bukti pembayaran. Sila cuba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const orderShortId = order.id.slice(-6).toUpperCase();
  const orderRefText = `ORD-${orderShortId}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Pembayaran Kod QR DuitNow"
      maxWidth="max-w-xl"
    >
      <div className="space-y-5 text-slate-800 text-xs">
        {/* Top Summary Banner */}
        <div className="bg-gradient-to-br from-red-50 to-amber-50/40 p-4 rounded-2xl border border-red-100 flex items-center justify-between gap-3">
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-asmm-red bg-white px-2 py-0.5 rounded-full border border-red-200">
              Imbas & Bayar QR Rasmi
            </span>
            <div className="font-extrabold text-slate-900 text-sm">
              {order.itemName} <span className="text-slate-500 font-medium">(x{order.quantity})</span>
            </div>
            <div className="text-[11px] text-slate-500">
              Rujukan Tempahan: <span className="font-mono font-bold text-slate-700">#{orderShortId}</span>
            </div>
          </div>

          <div className="text-right shrink-0">
            <span className="text-[10px] font-bold text-slate-500 uppercase block">Jumlah Perlu Dibayar</span>
            <span className="text-xl font-black text-asmm-red font-mono">
              RM {order.totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* QR Code Presentation Box */}
        <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#ED0080]/10 text-[#ED0080] rounded-full text-[11px] font-black tracking-wide uppercase mx-auto">
            <QrCode className="w-3.5 h-3.5" />
            <span>DuitNow QR Kebangsaan</span>
          </div>

          <div className="relative mx-auto w-52 h-52 bg-white p-3 rounded-2xl border-2 border-slate-200 shadow-sm flex items-center justify-center">
            <img
              src={settings.qrImageUrl || '/badges/qr-persatuan-asmm.png'}
              alt="DuitNow QR Persatuan Seni Silat Awang Sulung Merah Muda"
              className="w-full h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src.indexOf('/badges/qr-persatuan-asmm.png') === -1) {
                  target.src = '/badges/qr-persatuan-asmm.png';
                }
              }}
            />
          </div>

          <p className="text-[11px] text-slate-600 font-medium max-w-sm mx-auto leading-relaxed">
            Buka mana-mana aplikasi perbankan (<strong>MAE, CIMB, Bank Islam, TnG eWallet, GrabPay</strong>) dan imbas kod QR di atas.
          </p>
        </div>

        {/* Bank Account Details Card with 1-Click Copy */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-2.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500 font-bold flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              <span>Nama Bank:</span>
            </span>
            <span className="font-extrabold text-slate-900">{settings.bankName}</span>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
            <span className="text-slate-500 font-bold">Nombor Akaun:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-extrabold text-slate-900 text-xs sm:text-sm">
                {settings.accountNo}
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(settings.accountNo, 'acc')}
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[10px] font-extrabold transition-all flex items-center gap-1 cursor-pointer"
                title="Salin Nombor Akaun"
              >
                {isCopiedAcc ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{isCopiedAcc ? 'Disalin' : 'Salin'}</span>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
            <span className="text-slate-500 font-bold">Nama Akaun:</span>
            <span className="font-bold text-slate-700 text-right text-[10px] sm:text-[11px] max-w-[240px] truncate">
              {settings.accountHolder}
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100 bg-amber-50/50 p-2 rounded-xl border border-amber-200/60">
            <span className="text-amber-800 font-bold">Rujukan Pembayaran (Wajib):</span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-black text-amber-900">{orderRefText}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(orderRefText, 'ref')}
                className="px-2 py-0.5 bg-amber-200/80 hover:bg-amber-300 text-amber-900 rounded-md text-[10px] font-black transition-all flex items-center gap-1 cursor-pointer"
              >
                {isCopiedRef ? <Check className="w-2.5 h-2.5 text-emerald-800" /> : <Copy className="w-2.5 h-2.5" />}
                <span>{isCopiedRef ? 'Disalin' : 'Salin'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Verification Form */}
        <form onSubmit={handleSubmitProof} className="space-y-4 pt-1 border-t border-slate-100">
          <div className="space-y-1">
            <label className="block text-slate-800 font-extrabold text-xs">
              1. No. Rujukan Transaksi Perbankan (Pilihan/Disyorkan)
            </label>
            <input
              type="text"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="Contoh: 20261019984321 atau DuitNow Ref"
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-semibold text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-slate-800 font-extrabold text-xs">
              2. Muat Naik Resit / Tangkap Layar Pembayaran QR
            </label>
            
            {receiptImage ? (
              <div className="relative rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img
                    src={receiptImage}
                    alt="Pratonton Resit"
                    className="w-12 h-12 rounded-xl object-cover border border-emerald-200 shadow-2xs shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="text-[10px] font-black text-emerald-800 uppercase block">Resit Dilampirkan</span>
                    <span className="text-[11px] font-bold text-slate-700 truncate block">Imej bukti bayaran QR sedia dihantar</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReceiptImage('')}
                  className="p-1.5 hover:bg-emerald-100 text-slate-500 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                  title="Padam Imej"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 hover:border-asmm-red hover:bg-red-50/30 rounded-2xl transition-all bg-slate-50/60 group ${isUploadingReceipt ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
                <Upload className="w-6 h-6 text-slate-400 group-hover:text-asmm-red transition-colors mb-1" />
                <span className="text-xs font-extrabold text-slate-700 group-hover:text-asmm-red">
                  {isUploadingReceipt ? 'Sedang memuat naik ke Cloudinary...' : 'Klik untuk pilih atau tangkap gambar resit'}
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5">Format JPG, PNG, PDF (Disimpan di Cloudinary)</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  disabled={isUploadingReceipt}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
            >
              Bayar Nanti
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (!paymentRef.trim() && !receiptImage)}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isSubmitting ? 'Menghantar...' : 'Sahkan & Hantar Bukti Bayaran'}</span>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
